/**
 * Number Input Component for dev-tools
 * Part of Phase 3 refactoring - Component Decomposition (UI Components)
 */
import React from "react";

import { TextField } from "@mui/material";

import { FieldSchema } from "../../types";
import { formatFieldValue } from "../../utils/type-utils";

interface NumberInputProps {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  touched?: boolean;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  field,
  value,
  onChange,
  error,
  touched,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === "") {
      onChange(undefined);
      return;
    }

    if (field.type === "float" || field.type === "number") {
      const floatValue = parseFloat(inputValue);
      onChange(isNaN(floatValue) ? inputValue : floatValue);
    } else {
      // int32, uint32
      const intValue = parseInt(inputValue, 10);
      onChange(isNaN(intValue) ? inputValue : intValue);
    }
  };

  const getInputProps = () => {
    const baseProps: Record<string, unknown> = {
      step: field.type === "float" || field.type === "number" ? "any" : "1",
    };

    if (field.type === "uint32") {
      baseProps.min = 0;
      baseProps.max = 4294967295; // 2^32 - 1
    } else if (field.type === "int32") {
      baseProps.min = -2147483648; // -2^31
      baseProps.max = 2147483647; // 2^31 - 1
    }

    return baseProps;
  };

  return (
    <TextField
      type="number"
      name={field.name}
      label={`${field.name} (${field.type})`}
      value={formatFieldValue(value, field.type)}
      onChange={handleChange}
      error={!!error && touched}
      helperText={error && touched ? error : field.description}
      required={!field.isOptional}
      fullWidth
      size="small"
      variant="outlined"
      margin="dense"
      inputProps={getInputProps()}
      sx={{
        "& .MuiFormHelperText-root": {
          margin: "4px 0 0 0",
        },
      }}
    />
  );
};
